import React, { useCallback, useEffect, useState } from 'react'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { AIAgentType } from '../../../models/AIAgent'
import { type DocumentRecord } from '../../../models/Document'
import { type DocumentBatchChatInteractionResult } from '../../../models/ProcessDocument'
import { type ToolUseData } from '../../../models/StreamingAIAgent'
import { type User } from '../../../models/User'

import { documentFileToAttachment, useExecuteAiAgentMutation } from '../../../store/ai/aiAgent'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  resetDocumentSummaryEditorContent,
  selectDocument,
  setDocumentSummaryEditorContent,
  setDocumentSummaryResult,
} from '../../../store/processDocument/document'
import {
  selectDocumentBatchChatAiAgentId,
  selectDocumentBatchChatInteractionLoading,
  selectDocumentBatchDocuments,
  setDocumentBatchChatAiAgentId,
  setDocumentBatchChatInteractionLoading,
} from '../../../store/processDocument/documentBatch'
import { selectDocumentBatchChatDisabled, selectDocumentType } from '../../../store/processDocument/processDocument'
import { addMessage, selectLastUserChatMessage, setStreamingError, updateStreamingMessage } from '../../../store/streamingChat/streamingChat'

import { StreamingAIAgentChat } from '../../AIAgent/Chat/StreamingAIAgentChat'
import { useStreamingAiAgent } from '../../AIAgent/hooks/useStreamingAiAgent'

import { useFetchDocumentTypeTemplate } from '../DocumentSummary/hooks'

import { DocumentBatchChatHeader } from './DocumentBatchChatHeader'
import { DocumentBatchChatToolUse } from './DocumentBatchChatToolUse'
import { getEditDocumentSummaryToolInput } from './model'

const DOCUMENT_BATCH_CHAT_MAX_MESSAGE_LENGTH = 1000

interface DocumentBatchChatProps {
  contact: User
}

export const DocumentBatchChat: React.FC<DocumentBatchChatProps> = ({ contact }) => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const document = useAppSelector(selectDocument)
  const documentType = useAppSelector(selectDocumentType)
  const batchDocuments = useAppSelector(selectDocumentBatchDocuments)
  const lastUserInteraction = useAppSelector(selectLastUserChatMessage)

  const aiAgentId = useAppSelector(selectDocumentBatchChatAiAgentId)
  const interactionLoading = useAppSelector(selectDocumentBatchChatInteractionLoading)
  const chatDisabled = useAppSelector(selectDocumentBatchChatDisabled)

  const [interactionDocumentId, setInteractionDocumentId] = useState<string>()

  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()
  const { fetchDocumentTypeTemplate } = useFetchDocumentTypeTemplate()

  const documentId = document?.id
  const isInteractionDocumentInView = interactionDocumentId === documentId

  useEffect(() => {
    dispatch(setStreamingError(null))
  }, [dispatch, documentId])

  const onTextDelta = useCallback(
    (data: DocumentBatchChatInteractionResult) => {
      const { message } = data
      if (!message) {
        return
      }

      dispatch(updateStreamingMessage(message))
    },
    [dispatch],
  )

  const onNewAiAgentId = useCallback(
    (newAiAgentId: string) => {
      dispatch(setDocumentBatchChatAiAgentId(newAiAgentId))
    },
    [dispatch],
  )

  const onToolUseDelta = useCallback(
    (toolUse: ToolUseData) => {
      const editDocumentSummaryToolInput = getEditDocumentSummaryToolInput(toolUse)
      if (!editDocumentSummaryToolInput) {
        return
      }

      dispatch(setDocumentSummaryEditorContent(editDocumentSummaryToolInput.summary))
    },
    [dispatch],
  )

  const onMessageStop = useCallback(
    (toolUses: ToolUseData[]) => {
      const editDocumentSummaryToolInput = toolUses.map(getEditDocumentSummaryToolInput).find((editDocumentSummaryToolInput) => !!editDocumentSummaryToolInput)
      if (!editDocumentSummaryToolInput) {
        return
      }

      dispatch(setDocumentSummaryResult({ summary: editDocumentSummaryToolInput.summary }))
    },
    [dispatch],
  )

  const { triggerInteractWithAiAgentStreaming } = useStreamingAiAgent<DocumentBatchChatInteractionResult>({
    onTextDelta,
    onNewAiAgentId,
    onToolUseDelta,
    onMessageStop,
  })

  const executeDocumentBatchChatAiAgent = async (document: DocumentRecord) => {
    const { aiAgentId: executedAiAgentId } = await triggerExecuteAiAgent({
      accountId,
      payload: {
        type: AIAgentType.DOCUMENT_BATCH_CHAT,
        context: { documentBatchId: document.documentBatchId, contactId: contact.id, contactName: contact.name },
        attachments: batchDocuments.flatMap(({ files }) => files.map(documentFileToAttachment)),
      },
    }).unwrap()

    dispatch(setDocumentBatchChatAiAgentId(executedAiAgentId))

    return executedAiAgentId
  }

  const resolveDocumentBatchChatAiAgentId = async (document: DocumentRecord) => {
    if (aiAgentId) {
      return aiAgentId
    }

    return await executeDocumentBatchChatAiAgent(document)
  }

  const interactWithDocumentBatchChatAiAgent = async (document: DocumentRecord, query: string) => {
    setInteractionDocumentId(document.id)
    dispatch(setDocumentBatchChatInteractionLoading(true))
    try {
      const documentBatchChatAiAgentId = await resolveDocumentBatchChatAiAgentId(document)
      const currentDocumentSummaryTemplate = documentType ? await fetchDocumentTypeTemplate(documentType) : ''

      await triggerInteractWithAiAgentStreaming({
        accountId,
        aiAgentId: documentBatchChatAiAgentId,
        payload: { query, currentDocumentId: document.id, currentDocumentName: document.name, currentDocumentSummaryTemplate },
      })
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setDocumentBatchChatInteractionLoading(false))
      dispatch(resetDocumentSummaryEditorContent())
    }
  }

  const _handleDocumentBatchChatInteraction = async (chatMessage: string) => {
    if (!document) {
      return
    }

    dispatch(addMessage({ type: 'user', message: chatMessage }))
    await interactWithDocumentBatchChatAiAgent(document, chatMessage)
  }

  const handleDocumentBatchChatInteraction = tracked(_handleDocumentBatchChatInteraction, {
    eventName: TrackEvent.DOCUMENT_BATCH_CHAT_INTERACTED_WITH,
    eventDetails: { documentBatchId: document?.documentBatchId, documentId: document?.id },
  })

  const handleResubmit = async () => {
    if (!document || !lastUserInteraction) {
      return
    }

    await interactWithDocumentBatchChatAiAgent(document, lastUserInteraction.message)
  }

  return (
    <StreamingAIAgentChat
      handleSubmit={handleDocumentBatchChatInteraction}
      isLoading={interactionLoading}
      disabled={chatDisabled}
      handleResubmit={handleResubmit}
      header={<DocumentBatchChatHeader />}
      maxMessageLength={DOCUMENT_BATCH_CHAT_MAX_MESSAGE_LENGTH}
      renderToolUse={(toolUse) => <DocumentBatchChatToolUse toolUse={toolUse} contact={contact} isInteractionDocumentInView={isInteractionDocumentInView} />}
    />
  )
}
